import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Form,
  Input,
  List,
  Modal,
  Popover,
  Progress,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
  theme
} from "antd";
import { InfoCircleOutlined } from "@ant-design/icons";
import PageHeader from "../shared/PageHeader";
import Amount from "../shared/Amount";
import { formatCents, formatDate } from "../shared/money";
import ResponsiveTable from "../../../../design-system/ResponsiveTable";
import * as api from "./api";

const STATE_COLOR = {
  future: "default",
  open: "blue",
  ready: "gold",
  exported: "purple",
  transmitted: "green",
  overdue: "red"
};
const STATUS_COLOR = {
  draft: "default",
  ready: "gold",
  exported: "purple",
  transmitted: "green",
  accepted: "green",
  rejected: "red",
  corrected: "default"
};
const LEVEL_COLOR = { error: "red", warning: "orange", info: "blue" };
const TYPE_LABEL = {
  ustva: "Umsatzsteuer-Voranmeldung",
  dauerfristverlaengerung: "Dauerfristverlängerung",
  zm: "Zusammenfassende Meldung",
  bauabzugsteuer: "Bauabzugsteuer",
  lohnsteuer_anmeldung: "Lohnsteuer-Anmeldung"
};

const thisYear = new Date().getFullYear();
const years = Array.from({ length: 5 }, (_, i) => thisYear + 1 - i);
const pad = n => String(n).padStart(2, "0");

const useQuery = () => {
  const location = useLocation();
  return useMemo(() => new URLSearchParams(location.search), [location.search]);
};

// ---- shared filing actions (UStVA cards, "Weitere Meldungen" table) --------------------------

const useFilingActions = (meta, reload) => {
  const { t } = useTranslation("accounting");
  const [modal, setModal] = useState(null); // { kind: "transmit"|"mark", filing }
  const [busy, setBusy] = useState(false);
  const [form] = Form.useForm();

  const download = async filing => {
    try {
      await api.downloadXml(filing._id, `${filing.type}_${filing.period}.xml`);
      message.success(t("ELSTER XML downloaded"));
      reload();
    } catch (error) {
      message.error(api.errorText(error));
    }
  };
  const submitModal = async () => {
    try {
      const values = await form.validateFields();
      setBusy(true);
      if (modal.kind === "transmit") await api.transmit(modal.filing._id, values.pin);
      else await api.markTransmitted(modal.filing._id, values.transferTicket);
      message.success(t("Filing transmitted"));
      setModal(null);
      form.resetFields();
      reload();
    } catch (error) {
      if (error?.errorFields) return;
      message.error(api.errorText(error));
    } finally {
      setBusy(false);
    }
  };
  const correct = async filing => {
    try {
      await api.correct(filing._id);
      message.success(t("Correction filing created"));
      reload();
    } catch (error) {
      message.error(api.errorText(error));
    }
  };

  const buttons = filing => {
    if (!filing) return null;
    const open = ["ready", "exported"].includes(filing.status);
    const filed = ["transmitted", "accepted", "rejected"].includes(filing.status);
    const elsterReady = Boolean(meta?.elster?.configured);
    return (
      <div className="acct-actions">
        {(open || filed) && (
          <Button onClick={() => download(filing)}>{t("Download ELSTER XML")}</Button>
        )}
        {open && (
          <Button onClick={() => setModal({ kind: "mark", filing })}>
            {t("Mark as transmitted")}
          </Button>
        )}
        {open && (
          <Tooltip
            title={
              elsterReady
                ? ""
                : t(
                    "ELSTER transmission is not configured. Enter the certificate reference under ELSTER settings and ask the operator to connect the ERiC service."
                  )
            }
          >
            <Button
              type="primary"
              disabled={!elsterReady}
              onClick={() => setModal({ kind: "transmit", filing })}
            >
              {t("Transmit via ELSTER")}
            </Button>
          </Tooltip>
        )}
        {open && !elsterReady && (
          // The tooltip above is hover-only; this keeps the reason reachable on touch.
          <Popover
            trigger="click"
            overlayStyle={{ maxWidth: 320 }}
            content={t(
              "ELSTER transmission is not configured. Enter the certificate reference under ELSTER settings and ask the operator to connect the ERiC service."
            )}
          >
            <Button type="text" icon={<InfoCircleOutlined />} aria-label={t("ELSTER settings")} />
          </Popover>
        )}
        {filed && <Button onClick={() => correct(filing)}>{t("Create correction")}</Button>}
      </div>
    );
  };

  const modalNode = (
    <Modal
      rootClassName="acct-module"
      open={Boolean(modal)}
      title={modal?.kind === "transmit" ? t("Transmit via ELSTER") : t("Mark as transmitted")}
      onCancel={() => {
        setModal(null);
        form.resetFields();
      }}
      onOk={submitModal}
      confirmLoading={busy}
      okText={modal?.kind === "transmit" ? t("Transmit") : t("Confirm")}
      rootClassName="acct-module"
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        {modal?.kind === "transmit" ? (
          <>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 12 }}
              message={t(
                "The PIN of your ELSTER certificate is sent once with this request and is never stored."
              )}
            />
            <Form.Item
              name="pin"
              label={t("Certificate PIN")}
              rules={[{ required: true, message: t("Please enter the PIN") }]}
            >
              <Input.Password autoComplete="off" />
            </Form.Item>
            {meta?.elster?.testMode && (
              <Alert
                type="warning"
                showIcon
                message={t(
                  "Test mode is on: the filing is sent with the ELSTER test flag and is not legally effective."
                )}
              />
            )}
          </>
        ) : (
          <>
            <Typography.Paragraph>
              {t(
                "Confirm that you uploaded the XML in Mein ELSTER. Enter the Transferticket shown by ELSTER."
              )}
            </Typography.Paragraph>
            <Form.Item name="transferTicket" label={t("Transferticket")}>
              <Input />
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
  return { buttons, modalNode };
};

// ---- UStVA tab ---------------------------------------------------------------------------------

const KennzahlenTable = ({ meta, computation, previous }) => {
  const { t } = useTranslation("accounting");
  const rows = useMemo(() => {
    if (!computation?.kennzahlen) return [];
    const labels = new Map((meta?.kennzahlen || []).map(k => [k.kz, k]));
    return Object.keys(computation.kennzahlen)
      .filter(kz => kz !== "83")
      .map(kz => {
        const info = labels.get(kz) || { label: kz, kind: "tax" };
        const value = computation.kennzahlen[kz];
        const prev = previous?.kennzahlen?.[kz];
        const isBase = info.kind === "base";
        const tax = isBase ? (computation.formTax?.[kz] ?? null) : value;
        return {
          kz,
          label: info.label,
          base: isBase ? value : null,
          tax,
          prevBase: isBase ? prev : null,
          prevTax: isBase ? previous?.formTax?.[kz] : prev,
          show: value || prev
        };
      })
      .filter(r => r.show);
  }, [meta, computation, previous]);
  const euro = v =>
    v === null || v === undefined ? (
      ""
    ) : (
      <span className="acct-num">{`${new Intl.NumberFormat("de-DE").format(v)} €`}</span>
    );
  const cents = v =>
    v === null || v === undefined ? "" : <span className="acct-num">{formatCents(v)}</span>;
  return (
    <ResponsiveTable
      mobileLayout="table"
      className="acct-grid"
      size="small"
      pagination={false}
      rowKey="kz"
      dataSource={rows}
      columns={[
        { title: t("Kennzahl"), dataIndex: "kz", width: 80, render: kz => <Tag>{kz}</Tag> },
        { title: t("Description"), dataIndex: "label" },
        { title: t("Base (EUR)"), dataIndex: "base", align: "end", render: euro },
        { title: t("Tax"), dataIndex: "tax", align: "end", render: cents },
        {
          title: t("Previous period"),
          key: "prev",
          align: "end",
          render: (_, r) => (r.base !== null ? euro(r.prevBase ?? 0) : cents(r.prevTax ?? 0))
        }
      ]}
      summary={() => (
        <Table.Summary.Row>
          <Table.Summary.Cell index={0} colSpan={3}>
            <strong>{t("Kz 83 – VAT payable / refund")}</strong>
          </Table.Summary.Cell>
          <Table.Summary.Cell index={1} align="end">
            <Amount cents={computation?.amountDue || 0} strong />
          </Table.Summary.Cell>
          <Table.Summary.Cell index={2} align="end">
            <Amount cents={previous?.amountDue || 0} />
          </Table.Summary.Cell>
        </Table.Summary.Row>
      )}
    />
  );
};

const UstvaTab = ({ meta, initialPeriod }) => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const history = useHistory();
  const [year, setYear] = useState(initialPeriod ? Number(initialPeriod.slice(0, 4)) : thisYear);
  const [periods, setPeriods] = useState(null);
  const [selected, setSelected] = useState(initialPeriod || null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const loadPeriods = useCallback(async () => {
    try {
      const res = await api.ustvaPeriods(year);
      setPeriods(res.data);
    } catch (error) {
      message.error(api.errorText(error));
    }
  }, [year]);
  const loadDetail = useCallback(async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await api.ustvaPeriod(selected);
      setDetail(res.data);
    } catch (error) {
      message.error(api.errorText(error));
    } finally {
      setLoading(false);
    }
  }, [selected]);
  useEffect(() => {
    loadPeriods();
  }, [loadPeriods]);
  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const reload = () => {
    loadPeriods();
    loadDetail();
  };
  const { buttons, modalNode } = useFilingActions(meta, reload);
  const select = period => {
    setSelected(period);
    history.replace(`/user/accounting/tax?tab=ustva&period=${period}`);
  };
  const prepare = async () => {
    setPreparing(true);
    try {
      await api.prepareUstva(selected);
      message.success(t("Filing prepared"));
      reload();
    } catch (error) {
      message.error(api.errorText(error));
    } finally {
      setPreparing(false);
    }
  };

  const latestFiling = detail?.filings?.[0] || null;
  const blocking = (detail?.plausibility || []).some(f => f.level === "error");

  if (periods?.kleinunternehmer)
    return (
      <Alert
        type="info"
        showIcon
        message={t(
          "As a Kleinunternehmer (§ 19 UStG) you do not file a Umsatzsteuer-Voranmeldung."
        )}
      />
    );

  return (
    <div>
      <Space style={{ marginBottom: 16 }} wrap>
        <Select
          value={year}
          onChange={setYear}
          options={years.map(y => ({ value: y, label: y }))}
          style={{ minWidth: 110 }}
          aria-label={t("Year")}
        />
        {periods && (
          <Typography.Text type="secondary">
            {t("Filing interval")}:{" "}
            {periods.interval === "quarterly" ? t("quarterly") : t("monthly")}
            {periods.dauerfristverlaengerung ? ` · ${t("Dauerfristverlängerung active")}` : ""}
          </Typography.Text>
        )}
      </Space>
      <Row gutter={[12, 12]}>
        {(periods?.periods || []).map(p => (
          <Col xs={12} sm={8} md={6} lg={4} key={p.period}>
            <Card
              size="small"
              hoverable
              onClick={() => select(p.period)}
              style={{ borderColor: selected === p.period ? token.colorPrimary : undefined }}
            >
              <Space direction="vertical" size={2} style={{ width: "100%" }}>
                <strong>{p.label}</strong>
                <Tag color={STATE_COLOR[p.state]}>{t(`state_${p.state}`)}</Tag>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {t("Due")}: {formatDate(p.dueDate)}
                </Typography.Text>
                {p.amountDue !== null && <Amount cents={p.amountDue} strong />}
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
      {selected && (
        <Card
          style={{ marginTop: 16 }}
          title={
            <span className="acct-wrap" style={{ whiteSpace: "normal" }}>
              {`${t("Umsatzsteuer-Voranmeldung")} ${detail?.computation?.label || selected}`}
            </span>
          }
          extra={
            <Typography.Text type="secondary">
              {t("Due")}: {formatDate(detail?.dueDate)}
            </Typography.Text>
          }
        >
          {loading || !detail ? (
            <Spin />
          ) : (
            <Space direction="vertical" size={16} style={{ width: "100%" }}>
              <Row gutter={[16, 16]}>
                <Col xs={12} md={6}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Output VAT")}
                    value={formatCents(detail.computation.tax?.output || 0)}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Input VAT (Vorsteuer)")}
                    value={formatCents(detail.computation.tax?.input || 0)}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    className="acct-kpi"
                    title={
                      detail.computation.amountDue < 0 ? t("Refund") : t("VAT payable (Zahllast)")
                    }
                    value={formatCents(Math.abs(detail.computation.amountDue || 0))}
                    valueStyle={{
                      color: detail.computation.amountDue < 0 ? token.colorSuccess : undefined
                    }}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <Statistic
                    className="acct-kpi"
                    title={t("Entries")}
                    value={detail.computation.entryCount || 0}
                    suffix={
                      <Tag style={{ marginInlineStart: 8 }}>
                        {detail.computation.taxMethod === "ist"
                          ? t("Ist-Versteuerung")
                          : t("Soll-Versteuerung")}
                      </Tag>
                    }
                  />
                </Col>
              </Row>
              {detail.computation.roundingDifference ? (
                <Typography.Text type="secondary">
                  {t("Rounding difference to the ledger")}:{" "}
                  <Amount cents={detail.computation.roundingDifference} />
                </Typography.Text>
              ) : null}
              <KennzahlenTable
                meta={meta}
                computation={detail.computation}
                previous={detail.previous}
              />
              {detail.plausibility?.length ? (
                <List
                  size="small"
                  header={<strong>{t("Plausibility checks")}</strong>}
                  dataSource={detail.plausibility}
                  renderItem={f => (
                    <List.Item
                      actions={
                        f.link
                          ? [
                              <a key="l" href={f.link}>
                                {t("Open")}
                              </a>
                            ]
                          : []
                      }
                    >
                      <span className="acct-wrap">
                        <Tag color={LEVEL_COLOR[f.level]}>{t(`level_${f.level}`)}</Tag> {f.message}
                      </span>
                    </List.Item>
                  )}
                />
              ) : (
                <Alert type="success" showIcon message={t("No plausibility findings")} />
              )}
              {latestFiling && (
                <Descriptions size="small" bordered column={{ xs: 1, md: 3 }}>
                  <Descriptions.Item label={t("Status")}>
                    <Tag color={STATUS_COLOR[latestFiling.status]}>
                      {t(`filing_${latestFiling.status}`)}
                    </Tag>
                    {latestFiling.correctionOf ? <Tag>{t("Corrected filing")}</Tag> : null}
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Amount due")}>
                    <Amount cents={latestFiling.amountDue} strong />
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Transferticket")}>
                    {latestFiling.transferTicket || "–"}
                  </Descriptions.Item>
                </Descriptions>
              )}
              {blocking ? (
                <Typography.Text type="danger">
                  {t("Fix the errors in the plausibility checks first")}
                </Typography.Text>
              ) : null}
              <Space wrap>
                {(!latestFiling ||
                  ["ready", "exported", "draft"].includes(latestFiling.status)) && (
                  <Tooltip
                    title={blocking ? t("Fix the errors in the plausibility checks first") : ""}
                  >
                    <Button
                      type={latestFiling ? "default" : "primary"}
                      loading={preparing}
                      disabled={blocking}
                      onClick={prepare}
                    >
                      {latestFiling ? t("Re-prepare") : t("Prepare")}
                    </Button>
                  </Tooltip>
                )}
                {buttons(latestFiling)}
              </Space>
            </Space>
          )}
        </Card>
      )}
      {modalNode}
    </div>
  );
};

// ---- Weitere Meldungen -------------------------------------------------------------------------

const OtherFilingsTab = ({ meta }) => {
  const { t } = useTranslation("accounting");
  const [year, setYear] = useState(thisYear);
  const [filings, setFilings] = useState([]);
  const [zmPeriod, setZmPeriod] = useState(
    `${thisYear}-Q${Math.ceil((new Date().getMonth() + 1) / 3)}`
  );
  const [zm, setZm] = useState(null);
  const [dfYear, setDfYear] = useState(thisYear);
  const [df, setDf] = useState(null);
  const [bauPeriod, setBauPeriod] = useState(`${thisYear}-${pad(new Date().getMonth() + 1)}`);
  const [bau, setBau] = useState(null);

  const loadFilings = useCallback(async () => {
    try {
      const res = await api.listFilings({
        year,
        type: "zm,dauerfristverlaengerung,bauabzugsteuer,lohnsteuer_anmeldung"
      });
      setFilings(res.data.filings || []);
    } catch (error) {
      message.error(api.errorText(error));
    }
  }, [year]);
  useEffect(() => {
    loadFilings();
  }, [loadFilings]);
  const { buttons, modalNode } = useFilingActions(meta, loadFilings);

  const run = async (fn, setter) => {
    try {
      const res = await fn();
      if (setter) setter(res.data);
      else {
        message.success(t("Filing prepared"));
        loadFilings();
      }
    } catch (error) {
      message.error(api.errorText(error));
    }
  };
  const quarterOptions = years.flatMap(y =>
    [1, 2, 3, 4].map(q => ({ value: `${y}-Q${q}`, label: `Q${q}/${y}` }))
  );
  const monthOptions = years.flatMap(y =>
    Array.from({ length: 12 }, (_, i) => ({
      value: `${y}-${pad(i + 1)}`,
      label: `${pad(i + 1)}/${y}`
    }))
  );

  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={8}>
          <Card title={t("Zusammenfassende Meldung")} size="small">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Select
                value={zmPeriod}
                onChange={setZmPeriod}
                options={quarterOptions}
                style={{ width: "100%" }}
                showSearch
              />
              <Space wrap>
                <Button onClick={() => run(() => api.zmPreview(zmPeriod), setZm)}>
                  {t("Preview")}
                </Button>
                <Button type="primary" onClick={() => run(() => api.prepareZm(zmPeriod))}>
                  {t("Prepare")}
                </Button>
              </Space>
              {zm && (
                <>
                  <Typography.Text type="secondary">
                    {t("Due")}: {formatDate(zm.dueDate)} ·{" "}
                    {zm.monthlyRequired
                      ? t("Monthly ZM required (> 50,000 € intra-EU supplies)")
                      : t("Quarterly")}
                  </Typography.Text>
                  {zm.missingVatId?.length ? (
                    <Alert
                      type="error"
                      showIcon
                      message={`${zm.missingVatId.length} ${t("invoices without customer VAT ID")}`}
                    />
                  ) : null}
                  <ResponsiveTable
                    mobileLayout="table"
                    className="acct-grid"
                    size="small"
                    pagination={false}
                    rowKey={r => `${r.vatId}-${r.type}`}
                    dataSource={zm.lines}
                    columns={[
                      { title: t("VAT ID"), dataIndex: "vatId" },
                      { title: t("Customer"), dataIndex: "name" },
                      { title: t("Type"), dataIndex: "type", width: 60 },
                      {
                        title: t("Amount (EUR)"),
                        dataIndex: "amount",
                        align: "end",
                        render: v => <span className="acct-num">{v}</span>
                      }
                    ]}
                  />
                </>
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={t("Dauerfristverlängerung")} size="small">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Select
                value={dfYear}
                onChange={setDfYear}
                options={years.map(y => ({ value: y, label: y }))}
                style={{ width: "100%" }}
              />
              <Space wrap>
                <Button onClick={() => run(() => api.dauerfristPreview(dfYear), setDf)}>
                  {t("Preview")}
                </Button>
                <Button type="primary" onClick={() => run(() => api.prepareDauerfrist(dfYear))}>
                  {t("Prepare")}
                </Button>
              </Space>
              {df && (
                <Descriptions size="small" column={1} bordered>
                  <Descriptions.Item label={t("Sondervorauszahlung (1/11)")}>
                    <Amount cents={df.sondervorauszahlung} strong />
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Basis (previous year)")}>
                    <Amount cents={df.basis} />
                  </Descriptions.Item>
                  <Descriptions.Item label={t("Due")}>{formatDate(df.dueDate)}</Descriptions.Item>
                </Descriptions>
              )}
              {!meta?.settings?.dauerfristverlaengerung && (
                <Alert
                  type="info"
                  showIcon
                  message={t("Dauerfristverlängerung is not enabled in the accounting settings.")}
                />
              )}
            </Space>
          </Card>
        </Col>
        <Col xs={24} lg={8}>
          <Card title={t("Bauabzugsteuer")} size="small">
            <Space direction="vertical" style={{ width: "100%" }}>
              <Select
                value={bauPeriod}
                onChange={setBauPeriod}
                options={monthOptions}
                style={{ width: "100%" }}
                showSearch
              />
              <Space wrap>
                <Button onClick={() => run(() => api.bauabzugsteuerPreview(bauPeriod), setBau)}>
                  {t("Preview")}
                </Button>
                <Button
                  type="primary"
                  onClick={() => run(() => api.prepareBauabzugsteuer(bauPeriod))}
                >
                  {t("Prepare")}
                </Button>
              </Space>
              {bau && (
                <>
                  <Typography.Text type="secondary">
                    {t("Due")}: {formatDate(bau.dueDate)} · {t("Total withheld")}:{" "}
                    <Amount cents={bau.total} />
                  </Typography.Text>
                  <ResponsiveTable
                    size="small"
                    pagination={false}
                    rowKey="name"
                    dataSource={bau.lines}
                    columns={[
                      { title: t("Supplier"), dataIndex: "name", mobile: "title" },
                      {
                        title: t("Withheld (15 %)"),
                        dataIndex: "withheld",
                        align: "end",
                        mobile: "subtitle",
                        render: v => <Amount cents={v} strong />
                      },
                      { title: t("Tax number"), dataIndex: "taxNumber" },
                      {
                        title: t("Consideration"),
                        dataIndex: "gegenleistung",
                        align: "end",
                        render: v => <Amount cents={v} />
                      }
                    ]}
                  />
                </>
              )}
              {meta?.settings?.freistellungsbescheinigung?.validUntil && (
                <Alert
                  type={
                    meta.settings.freistellungsbescheinigung.daysLeft <= 28 ? "warning" : "info"
                  }
                  showIcon
                  message={`${t("Own Freistellungsbescheinigung valid until")} ${formatDate(meta.settings.freistellungsbescheinigung.validUntil)}`}
                />
              )}
            </Space>
          </Card>
        </Col>
      </Row>
      <Card
        size="small"
        title={t("Filings")}
        extra={
          <Select
            value={year}
            onChange={setYear}
            options={years.map(y => ({ value: y, label: y }))}
            style={{ minWidth: 100 }}
            aria-label={t("Year")}
          />
        }
      >
        <ResponsiveTable
          size="small"
          rowKey="_id"
          dataSource={filings}
          pagination={{ pageSize: 10 }}
          columns={[
            {
              title: t("Type"),
              dataIndex: "type",
              mobile: "title",
              render: (v, f) => (
                <span className="acct-wrap">
                  {TYPE_LABEL[v] || v} <span className="acct-num">{f.period}</span>
                </span>
              )
            },
            { title: t("Period"), dataIndex: "period", mobile: "hidden" },
            {
              title: t("Amount due"),
              dataIndex: "amountDue",
              align: "end",
              mobile: "subtitle",
              render: v => <Amount cents={v} large />
            },
            {
              title: t("Status"),
              dataIndex: "status",
              render: s => <Tag color={STATUS_COLOR[s]}>{t(`filing_${s}`)}</Tag>
            },
            { title: t("Due"), dataIndex: "dueDate", render: formatDate },
            { title: t("Transferticket"), dataIndex: "transferTicket" },
            { title: "", key: "actions", render: (_, f) => buttons(f) }
          ]}
        />
      </Card>
      {modalNode}
    </Space>
  );
};

// ---- Kleinunternehmer --------------------------------------------------------------------------

const KleinunternehmerTab = () => {
  const { t } = useTranslation("accounting");
  const { token } = theme.useToken();
  const [status, setStatus] = useState(null);
  useEffect(() => {
    api
      .kleinunternehmerStatus()
      .then(res => setStatus(res.data))
      .catch(error => message.error(api.errorText(error)));
  }, []);
  if (!status) return <Spin />;
  const meter = (title, m) => (
    <Card size="small" title={title}>
      <Progress
        percent={Math.min(100, m.percent)}
        status={m.level === "exceeded" ? "exception" : m.level === "warning" ? "active" : "normal"}
        strokeColor={
          m.level === "exceeded"
            ? token.colorError
            : m.level === "warning"
              ? token.colorWarning
              : undefined
        }
      />
      <Typography.Text>
        <Amount cents={m.amount} /> / <Amount cents={m.threshold} />{" "}
        <span className="acct-num">({m.percent} %)</span>
      </Typography.Text>
    </Card>
  );
  return (
    <Space direction="vertical" size={16} style={{ width: "100%" }}>
      <Alert
        type={status.kleinunternehmer ? "info" : "success"}
        showIcon
        message={
          status.kleinunternehmer
            ? t("The Kleinunternehmerregelung (§ 19 UStG) is active.")
            : t("Regular taxation: the thresholds are shown for information.")
        }
      />
      {status.warnings.map(w => (
        <Alert
          key={w}
          type={status.alertLevel === "exceeded" ? "error" : "warning"}
          showIcon
          message={w}
        />
      ))}
      <Row gutter={[16, 16]}>
        <Col xs={24} md={8}>
          {meter(
            `${t("Previous year")} ${status.previousYear.year} (${t("limit")} 25.000 €)`,
            status.previousYear
          )}
        </Col>
        <Col xs={24} md={8}>
          {meter(
            `${t("Current year")} ${status.currentYear.year} (${t("limit")} 100.000 €)`,
            status.currentYear
          )}
        </Col>
        <Col xs={24} md={8}>
          {meter(
            `${t("Current year")} ${status.currentYear.year} (${t("limit for next year")} 25.000 €)`,
            { ...status.nextYear, amount: status.currentYear.amount }
          )}
        </Col>
      </Row>
      <Typography.Text type="secondary">
        {t("Projected turnover for the year")}: <Amount cents={status.projected} />
      </Typography.Text>
    </Space>
  );
};

// ---- ELSTER settings ---------------------------------------------------------------------------

const ElsterSettingsTab = ({ meta, onSaved }) => {
  const { t } = useTranslation("accounting");
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (meta?.elster)
      form.setFieldsValue({
        certificateRef: meta.elster.certificateRef,
        testMode: meta.elster.testMode
      });
  }, [meta, form]);
  const save = async values => {
    setSaving(true);
    try {
      await api.updateElsterSettings(values);
      message.success(t("ELSTER settings saved"));
      onSaved();
    } catch (error) {
      message.error(api.errorText(error));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Card size="small" style={{ maxWidth: 640, width: "100%" }}>
      <Space direction="vertical" size={12} style={{ width: "100%" }}>
        <Alert
          type={meta?.elster?.serviceAvailable ? "success" : "warning"}
          showIcon
          message={
            meta?.elster?.serviceAvailable
              ? t("ERiC service connected")
              : t(
                  "No ERiC service connected: filings can be exported as XML and uploaded in Mein ELSTER."
                )
          }
        />
        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label={t("Tax number")}>
            {meta?.settings?.taxNumber || "–"}
          </Descriptions.Item>
          <Descriptions.Item label={t("ELSTER tax number (13 digits)")}>
            {meta?.elster?.elsterNumber || (
              <Typography.Text type="danger">
                {meta?.elster?.steuernummerError || "–"}
              </Typography.Text>
            )}
          </Descriptions.Item>
          <Descriptions.Item label={t("Bundesland")}>
            {meta?.settings?.bundesland || "–"}
          </Descriptions.Item>
        </Descriptions>
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item
            name="certificateRef"
            label={t("Certificate reference")}
            extra={t(
              "The reference under which your organisation certificate (.pfx) is stored in the ERiC service. The certificate file itself is never uploaded here."
            )}
          >
            <Input placeholder="z. B. mandant-4711" />
          </Form.Item>
          <Form.Item
            name="testMode"
            label={t("Test mode")}
            valuePropName="checked"
            extra={t(
              "Filings are sent with the ELSTER test flag and are not legally effective. Switch off once the certificate is productive."
            )}
          >
            <Switch />
          </Form.Item>
          <Typography.Paragraph type="secondary">
            {t("The certificate PIN is entered at transmission time, sent once and never stored.")}
          </Typography.Paragraph>
          <Button type="primary" htmlType="submit" loading={saving}>
            {t("Save")}
          </Button>
        </Form>
      </Space>
    </Card>
  );
};

// ---- page --------------------------------------------------------------------------------------

const Tax = () => {
  const { t } = useTranslation("accounting");
  const query = useQuery();
  const history = useHistory();
  const [meta, setMeta] = useState(null);
  const loadMeta = useCallback(() => {
    api
      .getMeta()
      .then(res => setMeta(res.data))
      .catch(error => message.error(api.errorText(error)));
  }, []);
  useEffect(() => {
    loadMeta();
  }, [loadMeta]);
  const tab = query.get("tab") || "ustva";
  return (
    <div>
      <PageHeader
        title={t("VAT & filings")}
        subtitle={t("Umsatzsteuer-Voranmeldung, ELSTER, Zusammenfassende Meldung, Bauabzugsteuer")}
      />
      <Tabs
        activeKey={tab}
        onChange={key => history.replace(`/user/accounting/tax?tab=${key}`)}
        items={[
          {
            key: "ustva",
            label: t("Umsatzsteuer"),
            children: <UstvaTab meta={meta} initialPeriod={query.get("period")} />
          },
          {
            key: "other",
            label: t("Weitere Meldungen"),
            children: <OtherFilingsTab meta={meta} />
          },
          {
            key: "kleinunternehmer",
            label: t("Kleinunternehmer"),
            children: <KleinunternehmerTab />
          },
          {
            key: "elster",
            label: t("ELSTER settings"),
            children: <ElsterSettingsTab meta={meta} onSaved={loadMeta} />
          }
        ]}
      />
    </div>
  );
};

export default Tax;
